(ns livefeed.util
  (:require
   [camel-snake-kebab.core :refer [->kebab-case-keyword]]
   [cljs.core :refer [PersistentQueue]]
   [clojure.string :as str]
   [clojure.walk :refer [postwalk]]
   [livefeed.i18n :refer [translate]]
   [livefeed.properties :refer [get-property]]))

(defn decode [s]
  (when s (js/decodeURIComponent s)))

(defn parse-kv [kv]
  (let [[k v] (str/split kv #"=")]
    [(keyword (decode k)) (decode v)]))

(defn querystring->map [qs]
  (let [qs (if (= \? (first qs))
             (subs qs 1)
             qs)]
    (->> (str/split qs #"&")
         (map parse-kv)
         (into {}))))

(defn indexed [coll]
  (map-indexed vector coll))

(defn queue
  ([]
   #queue [])
  ([coll]
   (PersistentQueue. nil (count coll) (seq coll) [] nil)))

(defn pop-n [q n]
  (-> (iterate pop q)
      (nth n)))

(defn ->kebab-case-keys [obj]
  (postwalk (fn [x]
              (if (map-entry? x)
                [(->kebab-case-keyword (key x)) (val x)]
                x))
            obj))

(defn throttle [f ms]
  (let [throttle? (atom false)]
    (fn [& args]
      (when-not @throttle?
        (reset! throttle? true)
        (apply f args)
        (js/setTimeout #(reset! throttle? false) ms)))))

(defn- linear-interpolation [x x0 x1 y0 y1]
  (/ (+ (* y0 (- x1 x)) (* y1 (- x x0)))
     (- x1 x0)))

(defn- clamp [v vmin vmax]
  (max vmin (min vmax (Math/floor v))))

(defn rate-limit
  "Calls f with evenly distributed intervals based on the size of the queue.
   min-interval: minimum ms between calls (when queue is large)
   max-interval: maximum ms between calls (when queue is small)
   first-interval: ms to wait before first call
   fast-queue-size: number of items at which min-interval is used."
  [f {:keys [min-interval max-interval first-interval fast-queue-size]}]
  (let [queue (atom (queue))
        processing? (atom false)]
    (fn [& args]
      (swap! queue conj args)
      (when-not @processing?
        (reset! processing? true)
        (letfn
         [(process-next []
            (when-let [next-args (peek @queue)]
              (apply f next-args)
              (swap! queue pop)
              (if (empty? @queue)
                (reset! processing? false)
                (js/setTimeout process-next
                               (clamp
                                (linear-interpolation (count @queue) 0
                                                      fast-queue-size
                                                      max-interval
                                                      min-interval)
                                min-interval
                                max-interval)))))]
          (js/setTimeout process-next first-interval))))))

(defn sum [xs]
  (reduce + 0 xs))

(defn format-percentage [p]
  (-> (float p)
      (* 100)
      (js/Math.floor)
      (str "%")))

(defn format-emojis [emojis]
  (let [images-api-url (get-property :images-api-url)]
    (into {} (for [emoji emojis]
               [(:id emoji) {:id (:id emoji)
                             :name (:name emoji)
                             :src (str images-api-url "/" (:ims-path emoji))}]))))

(defn- timestamp->ISO8601 [timestamp]
  (cond
    (instance? js/Date timestamp) (.toISOString timestamp)
    :else (str/replace timestamp #"Z.*" "Z")))

(defn- ->age-in-days [timestamp]
  (let [now (new js/Date)
        tomorrow-begins (new js/Date (.getFullYear now) (.getMonth now) (+ 1 (.getDate now)))
        time-diff (- tomorrow-begins timestamp)]
    (js/Math.floor (/ time-diff (* 24 60 60 1000)))))

(defn- ->age-in-years [timestamp]
  (let [now (new js/Date)
        next-year-begins (new js/Date (+ 1 (.getFullYear now)) 1 1)
        time-diff (- next-year-begins timestamp)]
    (js/Math.floor (/ time-diff (* 365 24 60 60 1000)))))

(defn- ->month [timestamp]
  (str (+ 1 (.getMonth timestamp))))

(defn- ->date [timestamp]
  (str (.getDate timestamp)))

(defn- ->year [timestamp]
  (.getFullYear timestamp))

(defn- ->hours [timestamp]
  (let [hours (.getHours timestamp)]
    (if (< hours 10)
      (str "0" hours)
      (str hours))))

(defn- ->minutes [timestamp]
  (let [minutes (.getMinutes timestamp)]
    (if (< minutes 10)
      (str "0" minutes)
      (str minutes))))

(defn- ->time [timestamp]
  (str (->hours timestamp) ":" (->minutes timestamp)))

(defn- ->date-year [timestamp show-full-date]
  (let [year (->year timestamp)
        date (str (->date timestamp) "." (->month timestamp) ".")
        date-or-yesterday (when (or (> (->age-in-days timestamp) 0) show-full-date)
                            (if (or (> (->age-in-days timestamp) 1) show-full-date)
                              date
                              (translate :yesterday)))]
    (when (or (> (->age-in-days timestamp) 0) show-full-date)
      (if (or (> (->age-in-years timestamp) 1) show-full-date)
        (str date-or-yesterday year " ")
        (str date-or-yesterday " ")))))

(defn timestamp->display-time [timestamp show-full-date]
  (when timestamp
    (let [iso8601 (timestamp->ISO8601 timestamp)
          timestamp-date (new js/Date iso8601)]
      (str (->date-year timestamp-date show-full-date) (->time timestamp-date)))))

(defn timestamp->full-date [timestamp]
  (timestamp->display-time timestamp true))

(defn local-storage-get [key default-value]
  (try
    (let [value (js/localStorage.getItem key)]
      (if value
        (js/JSON.parse value)
        default-value))
    (catch :default e
      (js/console.error "Error reading from localStorage:" e)
      default-value)))

(defn local-storage-set
  "Set value to local storage.
   If you ever add a new key to client's local storage,
   remember to update Evästekäytännöt-table. More info:
   https://docs.google.com/presentation/d/1wnPK72rYhxkUs-S11EjNy1duJEhM28Pkw9b8bOttFoE/edit?usp=sharing."
  [key value]
  (try
    (js/localStorage.setItem key (js/JSON.stringify value))
    (catch :default e
      (js/console.error "Error writing to localStorage:" e))))